import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Linkedin, Instagram, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { CircuitPaths, Reveal } from './TerminalEffects';
import { trackButtonClick } from '../lib/analytics';

export const Footer = () => {
  const ctaRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    if (ctaRef.current) obs.observe(ctaRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (visible) { const t = setTimeout(() => setButtonVisible(true), 2800); return () => clearTimeout(t); }
  }, [visible]);

  const words = ['Experiment.', 'Launch.', 'Scale.'];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Dramatic CTA Section */}
      <section ref={ctaRef} className="relative py-36 lg:py-48 overflow-hidden bg-[#0a0a0a]">
        <CircuitPaths />
        <div className="absolute inset-0 transition-all duration-[2s]" style={{
          background: visible ? 'radial-gradient(ellipse at 50% 50%, rgba(0,255,209,0.1) 0%, transparent 50%)' : 'none',
        }} />
        {/* Scanlines */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,209,0.1) 2px, rgba(0,255,209,0.1) 4px)',
        }} />

        <div className="relative z-20 max-w-[1200px] mx-auto px-6 lg:px-8 text-center">
          {/* Word-by-word dramatic reveal */}
          <div className="flex flex-wrap justify-center gap-x-[0.4em] mb-14">
            {words.map((word, i) => (
              <span key={`cta-word-${i}`}
                className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-[7rem] font-bold inline-block text-[#F5F0E0]"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: '-0.03em',
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0) scale(1)' : 'translateY(80px) scale(0.7)',
                  filter: visible ? 'blur(0px)' : 'blur(12px)',
                  transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 700 + 200}ms, transform 1s cubic-bezier(0.16,1,0.3,1) ${i * 700 + 200}ms, filter 0.8s ease ${i * 700 + 200}ms, text-shadow 0.8s ease ${i * 700 + 600}ms`,
                  textShadow: visible ? '0 0 60px rgba(0,255,209,0.3), 0 0 120px rgba(0,255,209,0.1)' : 'none',
                }}>
                {word}
              </span>
            ))}
          </div>

          {/* Animated line */}
          <div className="flex justify-center mb-10">
            <div className="h-px overflow-hidden transition-all duration-700" style={{
              width: buttonVisible ? '120px' : '0px',
              background: 'linear-gradient(90deg, transparent, #00FFD1, transparent)',
            }} />
          </div>

          {/* Build Now with glow pulse */}
          <div style={{
            opacity: buttonVisible ? 1 : 0,
            transform: buttonVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
            transition: 'opacity 0.6s cubic-bezier(0.16,1,0.3,1) 0.3s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.3s',
          }}>
            <Link to="/onboarding"
              onClick={() => trackButtonClick('build_now_cta', 'footer')}
              className={`inline-flex items-center gap-3 bg-[#00FFD1] text-black px-10 py-4 rounded-full text-sm font-bold uppercase tracking-[0.14em] hover:bg-[#7DFFD4] hover:scale-110 transition-all duration-300 group ${buttonVisible ? 'animate-cta-glow' : ''}`}
              style={{ fontFamily: "'Orbitron', sans-serif" }}
              data-testid="footer-cta-build-now">
              Build Now <ArrowRight size={16} className="group-hover:translate-x-1.5 transition-transform" />
            </Link>
          </div>
          <p className="mt-8 text-xs font-mono uppercase tracking-[0.2em]" style={{
            opacity: buttonVisible ? 0.4 : 0, color: '#F5F0E0', transition: 'opacity 0.8s ease 0.8s',
          }}>No credit card required</p>
        </div>
      </section>

      {/* Footer Links */}
      <footer className="bg-[#1a1a1a] pt-20 pb-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <Reveal>
              <div>
                <div className="cursor-pointer mb-6" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  <Logo height={36} />
                </div>
                <p className="text-sm text-[#F5F0E0]/50 mb-6 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Empowering SMEs with intelligent AI solutions that automate tasks, improve efficiency, and drive growth.
                </p>
                <div className="flex gap-3">
                  <a href="https://www.linkedin.com/company/111281484/admin/dashboard/" target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 bg-[#F5F0E0]/10 rounded-full flex items-center justify-center hover:bg-[#00FFD1] transition-all duration-300 group">
                    <Linkedin size={16} className="text-[#F5F0E0]/60 group-hover:text-[#2A2A2A]" />
                  </a>
                  <a href="https://www.instagram.com/hiboticsai?igsh=OGxmamw3N3gxNmNn&utm_source=qr" target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 bg-[#F5F0E0]/10 rounded-full flex items-center justify-center hover:bg-[#00FFD1] transition-all duration-300 group">
                    <Instagram size={16} className="text-[#F5F0E0]/60 group-hover:text-[#2A2A2A]" />
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div>
                <h4 className="text-xs font-mono uppercase tracking-[0.15em] text-[#C4956A] mb-6">Quick Links</h4>
                <ul className="space-y-3">
                  {[['Home', () => window.scrollTo({ top: 0, behavior: 'smooth' })], ['Services', () => scrollToSection('services')], ['How It Works', () => scrollToSection('how-it-works')], ['Industries', () => scrollToSection('industries')], ['Reviews', () => scrollToSection('reviews')]].map(([label, onClick]) => (
                    <li key={label}>
                      <button onClick={onClick} className="text-sm text-[#F5F0E0]/60 hover:text-[#00FFD1] transition-colors">{label}</button>
                    </li>
                  ))}
                  <li>
                    <Link to="/blog" className="text-sm text-[#F5F0E0]/60 hover:text-[#00FFD1] transition-colors" data-testid="footer-blog-link">Blog</Link>
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div>
                <h4 className="text-xs font-mono uppercase tracking-[0.15em] text-[#C4956A] mb-6">Our Services</h4>
                <ul className="space-y-3">
                  {['AI Voice Agents', 'AI Chatbots', 'AI Funnels', 'AI Websites', 'Custom Solutions'].map((s) => (
                    <li key={s} className="text-sm text-[#F5F0E0]/60">{s}</li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div>
                <h4 className="text-xs font-mono uppercase tracking-[0.15em] text-[#C4956A] mb-6">Get In Touch</h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Mail size={16} className="text-[#00FFD1] mt-0.5 flex-shrink-0" />
                    <a href="mailto:info@hiboticsai.com" className="text-sm text-[#F5F0E0]/60 hover:text-[#00FFD1] transition-colors">info@hiboticsai.com</a>
                  </li>
                  <li className="flex items-start gap-3">
                    <Phone size={16} className="text-[#00FFD1] mt-0.5 flex-shrink-0" />
                    <a href="tel:+447776752870" className="text-sm text-[#F5F0E0]/60 hover:text-[#00FFD1] transition-colors">+44 7776 752870</a>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin size={16} className="text-[#C4956A] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#F5F0E0]/60">Serving SMEs Worldwide</span>
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="h-px bg-[#F5F0E0]/10 mb-8" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#F5F0E0]/40 text-xs">&copy; {new Date().getFullYear()} Hibotics AI. All rights reserved.</p>
            <div className="flex flex-wrap justify-center md:justify-end gap-5">
              {[['Privacy Policy', '/privacy-policy'], ['Terms of Service', '/terms-of-service'], ['Cookie Policy', '/cookie-policy']].map(([label, href]) => (
                <a key={label} href={href} className="text-[#F5F0E0]/40 hover:text-[#00FFD1] transition-colors text-xs">{label}</a>
              ))}
              <button onClick={() => { localStorage.removeItem('cookieConsent'); window.location.reload(); }}
                className="text-[#F5F0E0]/40 hover:text-[#00FFD1] transition-colors text-xs">Cookie Settings</button>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};
